/**
 * Policy for Layout's global orchestrator error snackbar.
 * Page-owned failures (auth forms, mutations with local Alerts) must suppress.
 */

export type AdminGlobalErrorPolicyInput = {
  /** Set on axios request config when the caller shows its own error UI. */
  suppressGlobalErrorNotification?: boolean;
  /** Relative or absolute request URL from axios `config.url`. */
  url?: string | null;
};

const AUTH_OWNED_PATH =
  /\/auth\/(me|login|signup|logout|email|password-reset|mfa|oauth|otp|passkeys)(\/|$|\?)/i;

function requestPath(url: string): string {
  const raw = url.trim();
  if (!raw) return '';
  try {
    if (/^https?:\/\//i.test(raw)) {
      return new URL(raw).pathname;
    }
  } catch {
    /* fall through */
  }
  return raw.split('?')[0] || '';
}

/**
 * True when Layout must not open the global snackbar for this orchestrator failure.
 */
export function shouldSuppressAdminOrchestratorGlobalError(
  input: AdminGlobalErrorPolicyInput,
): boolean {
  if (input.suppressGlobalErrorNotification === true) {
    return true;
  }
  const path = requestPath(String(input.url ?? ''));
  if (!path) {
    return false;
  }
  // Match `/auth/me` and `/auth/login` even when prefixed by mount path.
  if (AUTH_OWNED_PATH.test(path) || /\/auth\/me\/?$/.test(path)) {
    return true;
  }
  return false;
}

const TECHNICAL_ADMIN_ERROR_PATTERNS = [
  /Adapter step\s+"/i,
  /Provider income failed/i,
  /HttpStatusCode/i,
  /FlinksCode/i,
  /income returned \d{3}/i,
  /\{[\s\S]*"FlinksCode"/i,
  /\{[\s\S]*"HttpStatusCode"/i,
];

/** True when a message looks like an upstream adapter/provider dump. */
export function looksLikeTechnicalAdminError(message: string): boolean {
  const normalized = message.trim();
  if (!normalized) return false;
  return TECHNICAL_ADMIN_ERROR_PATTERNS.some((pattern) => pattern.test(normalized));
}

const GLOBAL_SNACKBAR_FALLBACK =
  'Something went wrong. Please try again or check service logs.';

/**
 * Defense in depth for Layout snackbar — never show Flinks/adapter JSON blobs.
 */
export function resolveAdminGlobalSnackbarMessage(message: string): string {
  const cleaned = message
    .replace(/\s*-\s*Flinks\s+Data\s*:.*$/is, '')
    .replace(/\{[\s\S]*\}$/s, '')
    .trim();
  if (!cleaned || looksLikeTechnicalAdminError(cleaned) || looksLikeTechnicalAdminError(message)) {
    return GLOBAL_SNACKBAR_FALLBACK;
  }
  return cleaned.length > 400 ? `${cleaned.slice(0, 397)}...` : cleaned;
}

/** Axios config fragment for mutations/forms that render their own Alert. */
export const SUPPRESS_ADMIN_GLOBAL_ERROR = {
  suppressGlobalErrorNotification: true,
} as const;
